import React from 'react'
import {cn} from '../../../lib/utils'
import {Button} from '@fitro/customer-ui/Button'
import {useTranslation} from '../../../hooks/useTranslation'
import {
    consumeTimedClickSuppression,
    isPenPointer,
    setTimedClickSuppression,
    suppressNextCompatibleClick,
} from '../../../lib/pointer-events'

interface ModalHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
    title?: string
    subtitle?: string
    children?: React.ReactNode
    startContent?: React.ReactNode
    endContent?: React.ReactNode
    showCloseButton?: boolean
    onClose?: () => void
    variant?: 'default' | 'elevated' | 'minimal'
    titleAs?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

const headerVariants = {
    default: [
        'border-b border-gray-200 dark:border-gray-700',
        'bg-white dark:bg-gray-900'
    ].join(' '),

    elevated: [
        'border-b border-gray-200 dark:border-gray-700',
        'bg-gray-50 dark:bg-gray-800',
        'shadow-sm'
    ].join(' '),

    minimal: [
        'bg-white dark:bg-gray-900'
    ].join(' ')
}

const PEN_TAP_MOVE_TOLERANCE_PX = 8

type PenCloseTap = {
    pointerId: number
    target: EventTarget
    clientX: number
    clientY: number
}

export const ModalHeader: React.FC<ModalHeaderProps> = ({
                                                            title,
                                                            subtitle,
                                                            children,
                                                            startContent,
                                                            endContent,
                                                            showCloseButton = true,
                                                            onClose,
                                                            variant = 'default',
                                                            titleAs = 'h2',
                                                            className,
                                                        ...props
                                                    }) => {
    const {t} = useTranslation('common')
    const suppressNextCloseClickRef = React.useRef<boolean | null>(null)
    const suppressCloseClickTimeoutRef = React.useRef<number | null>(null)
    const penCloseTapRef = React.useRef<PenCloseTap | null>(null)
    const hasContent = title || subtitle || children || startContent
    const hasActions = showCloseButton || endContent

    if (!hasContent && !hasActions) return null

    const TitleComponent = titleAs

    const handleCloseClick = () => {
        if (consumeTimedClickSuppression(suppressNextCloseClickRef, true, suppressCloseClickTimeoutRef)) return

        onClose?.()
    }

    const handleClosePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
        if (!isPenPointer(event) || event.isPrimary === false || event.button !== 0) {
            penCloseTapRef.current = null
            return
        }

        penCloseTapRef.current = {
            pointerId: event.pointerId,
            target: event.currentTarget,
            clientX: event.clientX,
            clientY: event.clientY,
        }
    }

    const handleClosePointerUp = (event: React.PointerEvent<HTMLButtonElement>) => {
        const penTap = penCloseTapRef.current
        penCloseTapRef.current = null
        if (!isPenPointer(event) || event.isPrimary === false || !penTap) return
        if (penTap.pointerId !== event.pointerId || penTap.target !== event.currentTarget) return
        if (Math.hypot(event.clientX - penTap.clientX, event.clientY - penTap.clientY) > PEN_TAP_MOVE_TOLERANCE_PX) return

        event.preventDefault()
        event.stopPropagation()
        suppressNextCompatibleClick(event)
        setTimedClickSuppression(suppressNextCloseClickRef, true, suppressCloseClickTimeoutRef)
        onClose?.()
    }

    const clearPenCloseTap = () => {
        penCloseTapRef.current = null
    }

    return (
        <div
            className={cn(
                'flex items-center justify-between',
                'p-6 md:rounded-t-xl',
                headerVariants[variant],
                className
            )}
            {...props}
        >

            <div className="flex items-center gap-4 min-w-0 flex-1">
                {startContent && (
                    <div className="shrink-0">
                        {startContent}
                    </div>
                )}


                {(title || subtitle || children) && (
                    <div className="min-w-0 flex-1">
                        {title && (
                            <TitleComponent
                                className="text-xl font-semibold text-gray-900 dark:text-gray-100 break-words mb-1"
                            >
                                {title}
                            </TitleComponent>
                        )}
                        {subtitle && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                                {subtitle}
                            </p>
                        )}
                        {children && (
                            <div className="mt-2">
                                {children}
                            </div>
                        )}
                    </div>
                )}
            </div>


            {hasActions && (
                <div className="flex items-center gap-2 ml-4 shrink-0">
                    {endContent && (
                        <div className="flex items-center gap-2">
                            {endContent}
                        </div>
                    )}

                    {showCloseButton && onClose && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="small"
                            iconOnly
                            onClick={handleCloseClick}
                            onPointerDown={handleClosePointerDown}
                            onPointerUp={handleClosePointerUp}
                            onPointerCancel={clearPenCloseTap}
                            onPointerLeave={clearPenCloseTap}
                            className={cn(
                                'min-w-12 min-h-12 w-12 h-12',
                                'rounded-lg',
                                'hover:bg-gray-100 dark:hover:bg-gray-800',
                                'focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                                'transition-colors duration-200'
                            )}
                            aria-label={t('actions.close')}
                        >
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </Button>
                    )}
                </div>
            )}
        </div>
    )
}

ModalHeader.displayName = 'ModalHeader'
